import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface DatePickerProps {
  onDateChange: (startDate: Date | null, endDate: Date | null) => void;
}

export const DatePicker: React.FC<DatePickerProps> = ({ onDateChange }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  // Helper functions to handle date manipulation
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const jsDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return (jsDay === 0 ? 6 : jsDay - 1);
  };

  const getMonthData = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = getDaysInMonth(date);
    const firstDayOfMonth = getFirstDayOfMonth(date);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('es-ES', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isDateDisabled = (date: Date) => {
  // Disable dates in the past and also today and tomorrow
  const tomorrow = new Date();
  tomorrow.setDate( new Date().getDate() + 1);
  return date.getTime() <= tomorrow.getTime();
  };

/*
  TODO: Review if this function is needed
  const isPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  }; 
  
  */

  const isDateSelected = (date: Date) => {
    if (!date) return false;
    
    if (selectedStartDate && selectedEndDate) {
      const dateTime = date.getTime();
      return (
        dateTime === selectedStartDate.getTime() ||
        dateTime === selectedEndDate.getTime()
      );
    }
    
    if (selectedStartDate && !selectedEndDate) {
      return date.getTime() === selectedStartDate.getTime();
    }
    
    return false;
  };

  const isDateInRange = (date: Date) => {
    if (!date || !selectedStartDate) return false;
    
    if (selectedEndDate) {
      const dateTime = date.getTime();
      return (
        dateTime > selectedStartDate.getTime() &&
        dateTime < selectedEndDate.getTime()
      );
    }
    
    if (hoverDate && selectedStartDate) {
      const dateTime = date.getTime();
      const startTime = selectedStartDate.getTime();
      const hoverTime = hoverDate.getTime();
      
      return (
        (dateTime > startTime && dateTime < hoverTime) ||
        (dateTime < startTime && dateTime > hoverTime)
      );
    }
    
    return false;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (date: Date) => {
    if (isDateDisabled(date)) return;

    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
      onDateChange(date, null);
    } else {
      if (date < selectedStartDate) {
        setSelectedEndDate(selectedStartDate);
        setSelectedStartDate(date);
        onDateChange(date, selectedStartDate);
      } else {
        setSelectedEndDate(date);
        onDateChange(selectedStartDate, date);
      }
    }
  };

  const handleDateHover = (date: Date) => {
    if (selectedStartDate && !selectedEndDate && !isDateDisabled(date)) {
      setHoverDate(date);
    }
  };

  const days = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];
  const monthData = getMonthData(currentMonth);

  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={handlePrevMonth}
          className="p-1 rounded-full hover:bg-gray-100"
        >
          <ChevronLeft size={20} />
        </button>
        <h3 className="font-medium">
          {currentMonth.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
        </h3>
        <button
          onClick={handleNextMonth}
          className="p-1 rounded-full hover:bg-gray-100"
        >
          <ChevronRight size={20} />
        </button>
      </div>
      
      <div className="grid grid-cols-7 gap-1 mb-2">
        {days.map((day) => (
          <div key={day} className="py-1 text-xs font-medium text-center text-gray-500">
            {day}
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {monthData.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="h-8"></div>;
          }

          const isDisabled = isDateDisabled(date);
          const isSelected = isDateSelected(date);
          const isInRange = isDateInRange(date);
          
          let className = "flex items-center justify-center h-8 text-sm rounded-full cursor-pointer calendar-day";
          
          if (isDisabled) {
            className += " calendar-day-disabled text-gray-300";
          } else if (isSelected) {
            className += " calendar-day-selected";
          } else if (isInRange) {
            className += " calendar-day-range";
          }
          
          return (
            <div
              key={date.toISOString()}
              className={className}
              onClick={() => !isDisabled && handleDateClick(date)}
              onMouseEnter={() => handleDateHover(date)}
            >
              {date.getDate()}
            </div>
          );
        })}
      </div>
      
      {(selectedStartDate || selectedEndDate) && (
        <div className="pt-4 mt-4 border-t border-gray-200">
          <div className="text-sm">
            <div className="flex justify-between text-gray-600">
              <div>
                <div className="text-xs font-medium uppercase">Entrada</div>
                <div className="font-medium">
                  {selectedStartDate ? formatDate(selectedStartDate) : '-'}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium uppercase">Salida</div>
                <div className="font-medium">
                  {selectedEndDate ? formatDate(selectedEndDate) : '-'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};